import { useState, useMemo, useEffect } from 'react';
import { getTimeIntervals, toLogicDate } from '@/utils/date-utils';
import { DB } from '@services/database.service';
import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { useComposerStore } from '@stores/v4/useComposerStore';
import { useProgramManifestStore } from '@stores/v4/useProgramManifestStore';
import { useUIStore } from '@stores/v4/useUIStore';

/**
 * useEventComposer: Headless logic for the polymorphic event editor.
 * REFACTORED: Decoupled from UI, 100% testable.
 * HARDENED: Robust state selectors to ensure UI re-renders on every mutation.
 */
export const useEventComposer = () => {
    // 1. SELECTORS (Explicit bindings for reactivity)
    const composedEvent = useComposerStore(state => state.composedEvent);
    const closeComposer = useComposerStore(state => state.closeComposer);
    const updateComposedEvent = useComposerStore(state => state.updateComposedEvent);
    const updateComposedPayload = useComposerStore(state => state.updateComposedPayload);
    const updatePreviewData = useComposerStore(state => state.updatePreviewData);
    const saveComposedEvent = useComposerStore(state => state.saveComposedEvent);
    const deleteComposedEvent = useComposerStore(state => state.deleteComposedEvent);
    const startSessionFromComposer = useComposerStore(state => state.startSessionFromComposer);
    const updatePerformanceLog = useComposerStore(state => state.updatePerformanceLog);

    const { programs, fetchProgramManifest, programDays, fetchProgramDays } = useProgramManifestStore();
    const { isLoading } = useUIStore();

    const [selectedProgramId, setSelectedProgramId] = useState<string | null>(null);
    const [templateDetails, setTemplateDetails] = useState<any | null>(null);
    const [localTitle, setLocalTitle] = useState<string>("");

    // --- SYNC LOCAL TITLE ---
    useEffect(() => {
        if (composedEvent?.title !== undefined) {
            setLocalTitle(composedEvent.title);
        }
    }, [composedEvent?.id, composedEvent?.title]);

    // --- DYNAMIC TIME INTERVALS ---
    const timeIntervals = useMemo(() => {
        const standard = getTimeIntervals();
        if (!composedEvent) return standard;
        const list = [...standard];
        if (composedEvent.startTime && !list.includes(composedEvent.startTime)) list.push(composedEvent.startTime);
        if (composedEvent.endTime && !list.includes(composedEvent.endTime)) list.push(composedEvent.endTime);
        return list.sort();
    }, [composedEvent?.startTime, composedEvent?.endTime]);

    // --- HYDRATION & PREVIEWS ---
    useEffect(() => {
        if (!programs || programs.length === 0) fetchProgramManifest();
    }, [programs, fetchProgramManifest]);

    useEffect(() => {
        if (composedEvent?.payload?.parent_program_id) {
            setSelectedProgramId(composedEvent.payload.parent_program_id);
        }
    }, [composedEvent?.id, composedEvent?.payload?.parent_program_id]);

    useEffect(() => {
        if (selectedProgramId) fetchProgramDays(selectedProgramId);
    }, [selectedProgramId, fetchProgramDays]);

    useEffect(() => {
        const loadPreview = async () => {
            if (composedEvent?.programDayId) {
                const { data } = await DB.fetchSessionTemplate(composedEvent.programDayId);
                if (data && data.blocks) {
                    data.blocks.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
                    data.blocks.forEach((b: any) => {
                        if (b.block_items) b.block_items.sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
                    });
                }
                setTemplateDetails(data);
            } else {
                setTemplateDetails(null);
            }
        };
        loadPreview();
    }, [composedEvent?.programDayId]);

    // --- EVENT HANDLERS ---
    const handleProgramDaySelect = (dayId: string) => {
        const day = (programDays || []).find((d: any) => d.id === dayId);
        updateComposedEvent({ 
            programDayId: dayId, 
            title: day?.label || "",
            type: EVENT_TYPES.PERFORMANCE,
            payload: {
                ...composedEvent?.payload,
                parent_program_id: selectedProgramId,
                program_day_label: day?.label
            }
        });
    };

    const handleTypeChange = (newType: string) => {
        if (!composedEvent) return;
        const oldType = composedEvent.type;
        
        // Prevent cross-pollution of items between distinct modules
        const needsPayloadReset = 
            (oldType === EVENT_TYPES.NUTRITION || oldType === EVENT_TYPES.SUPPLEMENTATION || oldType === 'SUPPLEMENT') &&
            (newType === EVENT_TYPES.NUTRITION || newType === EVENT_TYPES.SUPPLEMENTATION || newType === 'SUPPLEMENT') &&
            oldType !== newType;

        if (needsPayloadReset) {
            updateComposedEvent({ 
                type: newType, 
                payload: { ...composedEvent.payload, items: [] } 
            });
        } else {
            updateComposedEvent({ type: newType });
        }
    };

    const handleUpdatePayload = (updates: any) => {
        updateComposedPayload(updates);
    };

    const isPastDate = useMemo(() => {
        if (!composedEvent) return false;
        const today = toLogicDate(new Date());
        return composedEvent.startDate < today;
    }, [composedEvent?.startDate]);

    return {
        composedEvent,
        isLoading,
        programs,
        programDays,
        selectedProgramId,
        setSelectedProgramId,
        templateDetails,
        localTitle,
        setLocalTitle,
        timeIntervals,
        isPastDate,
        closeComposer,
        updateComposedEvent,
        updatePreviewData,
        saveComposedEvent,
        deleteComposedEvent,
        startSessionFromComposer,
        updatePerformanceLog,
        handleProgramDaySelect,
        handleTypeChange,
        handleUpdatePayload
    };
};
